import { Controller } from '@hotwired/stimulus'

/**
 * Время, за которое текст проходит полную длину SVG-пути.
 *
 * @type {number}
 */
const DURATION = 35000

/**
 * Контроллер бесшовно прокручивает повторяющийся текст по SVG textPath.
 *
 * Анимация запускается только пока секция с контроллером видна во viewport:
 * IntersectionObserver включает и выключает requestAnimationFrame, а текущее
 * смещение сохраняется между паузами.
 *
 * @extends Controller
 */
export default class TextPathScrollController extends Controller {
  static targets = ['path', 'svg']

  /**
   * Инициализирует состояние анимации, ResizeObserver и IntersectionObserver.
   *
   * Измерение текста откладывается до готовности шрифтов, чтобы расчет ширины
   * совпадал с реальным SVG-рендерингом.
   *
   * @returns {void}
   */
  connect() {
    this._offset = 0
    this._lastTime = null
    this._repLength = 0
    this._pathLength = 0
    this._raf = null
    this._originalHTML = null
    this._resizeTimer = null
    this._isConnected = true
    this._isReady = false
    this._isVisible = false
    this._isSetup = false
    this._tick = this._tick.bind(this)
    this._resizeObserver = new ResizeObserver(() => {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => this._reset(), 150)
    })

    if ('IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver(([entry]) => {
        this._isVisible = entry.isIntersecting

        if (this._isVisible) {
          this._startWhenReady()
        } else {
          this._stop()
        }
      })
      this._intersectionObserver.observe(this.element)
    } else {
      this._isVisible = true
    }

    document.fonts.ready.then(() => {
      if (!this._isConnected) return

      this._isReady = true
      requestAnimationFrame(() => this._startWhenReady())
    })
  }

  /**
   * Останавливает анимацию и снимает все наблюдатели при отключении контроллера.
   *
   * @returns {void}
   */
  disconnect() {
    this._isConnected = false
    this._stop()
    this._resizeObserver.disconnect()
    this._intersectionObserver?.disconnect()
    clearTimeout(this._resizeTimer)
  }

  /**
   * Сбрасывает сгенерированные повторы текста и заново измеряет SVG.
   *
   * Используется при изменении размера SVG, чтобы количество повторов и длины
   * оставались актуальными для текущего layout.
   *
   * @returns {void}
   * @private
   */
  _reset() {
    this._stop()
    this._lastTime = null
    // Возвращаем исходный HTML перед повторным измерением и заполнением.
    if (this._originalHTML !== null) {
      this.pathTarget.innerHTML = this._originalHTML
    }
    this._isSetup = false
    this._startWhenReady()
  }

  /**
   * Запускает первичную настройку или продолжает анимацию, когда все готово.
   *
   * Контроллер должен быть подключен, шрифты должны загрузиться, а секция
   * должна быть видима на экране.
   *
   * @returns {void}
   * @private
   */
  _startWhenReady() {
    if (!this._isConnected || !this._isReady || !this._isVisible) return

    if (this._isSetup) {
      this._start()
    } else {
      this._setup()
    }
  }

  /**
   * Запускает requestAnimationFrame, если анимация еще не запущена.
   *
   * @returns {void}
   * @private
   */
  _start() {
    if (this._raf !== null) return

    this._lastTime = null
    this._raf = requestAnimationFrame(this._tick)
  }

  /**
   * Отменяет активный requestAnimationFrame.
   *
   * @returns {void}
   * @private
   */
  _stop() {
    if (this._raf === null) return

    cancelAnimationFrame(this._raf)
    this._raf = null
  }

  /**
   * Измеряет SVG-путь и исходный текст, затем заполняет textPath повторами.
   *
   * Дополнительный скрытый SVG text используется как probe: так браузер
   * возвращает ширину текста с теми же шрифтами и стилями, что и у реального
   * textPath.
   *
   * @returns {void}
   * @private
   */
  _setup() {
    const svg = this.hasSvgTarget ? this.svgTarget : this.element
    const svgPath = svg.querySelector('path')
    this._pathLength = svgPath ? svgPath.getTotalLength() : 1000

    // Сохраняем исходный HTML один раз для будущих пересчетов.
    if (this._originalHTML === null) {
      this._originalHTML = this.pathTarget.innerHTML
      this._resizeObserver.observe(svg)
    }

    // Измеряем текст внутри того же SVG, чтобы шрифты и размеры совпадали.
    // Обычный <text> не ограничен путем, поэтому getBBox() возвращает реальную ширину.
    const ns = 'http://www.w3.org/2000/svg'
    const probe = document.createElementNS(ns, 'text')
    probe.setAttribute('x', '0')
    probe.setAttribute('y', '0')
    probe.style.cssText = 'visibility:hidden;pointer-events:none;'
    // Копируем вычисленные стили, чтобы применились font-size/family из CSS.
    const srcText = this.pathTarget.closest('text')
    if (srcText) {
      const cs = window.getComputedStyle(srcText)
      probe.style.fontSize = cs.fontSize
      probe.style.fontFamily = cs.fontFamily
      probe.style.fontWeight = cs.fontWeight
    }
    probe.innerHTML = this.pathTarget.innerHTML.repeat(2)
    svg.appendChild(probe)

    // Измеряем ширину одного повтора по точке начала второго повтора.
    // Две копии нужны, чтобы учесть завершающие пробелы: getComputedTextLength()
    // обрезает их на последнем повторе и из-за этого может появиться скачок.
    let repLength = 0
    try {
      const totalChars = probe.getNumberOfChars()
      const charsPerRep = Math.round(totalChars / 2)
      if (charsPerRep > 0 && charsPerRep < totalChars) {
        repLength = probe.getStartPositionOfChar(charsPerRep).x
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      // Игнорируем и пробуем следующий способ измерения.
    }
    if (!repLength) {
      try {
        repLength = probe.getComputedTextLength() / 2
      } catch (error) {
        console.error(
          'Failed to measure text width with getComputedTextLength()',
          error
        )
      }
    }
    if (!repLength) {
      try {
        repLength = probe.getBBox().width / 2
      } catch (error) {
        console.error('Failed to measure text width with getBBox()', error)
      }
    }
    svg.removeChild(probe)

    // Fallback: считаем, что исходный повтор примерно заполняет путь.
    this._repLength = repLength || this._pathLength

    // Заполняем путь текстом с буфером: один повтор позади и один впереди.
    const neededReps = Math.ceil(this._pathLength / this._repLength) + 3
    const blocks = neededReps
    if (blocks > 1) {
      const original = this.pathTarget.innerHTML
      this.pathTarget.innerHTML = original.repeat(blocks)
    }

    // Начинаем на один повтор до старта пути, чтобы текст въезжал из-за края.
    this._offset = -this._repLength
    this.pathTarget.setAttribute('startOffset', this._offset)

    this._lastTime = null
    this._isSetup = true
    this._start()
  }

  /**
   * Передвигает textPath на один animation frame.
   *
   * @param {DOMHighResTimeStamp} timestamp Время текущего кадра от браузера.
   * @returns {void}
   * @private
   */
  _tick(timestamp) {
    this._raf = null
    if (!this._isVisible) return

    if (this._lastTime !== null) {
      const speed = this._pathLength / DURATION // SVG-единиц в миллисекунду.
      this._offset += (timestamp - this._lastTime) * speed

      // Бесшовный цикл: текст повторяется каждые _repLength SVG-единиц.
      // Шаг назад на один повтор визуально идентичен продолжению движения.
      if (this._offset >= 0) {
        this._offset -= this._repLength
      }

      this.pathTarget.setAttribute('startOffset', this._offset)
    }
    this._lastTime = timestamp
    this._raf = requestAnimationFrame(this._tick)
  }
}
